import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../Common/Hooks/useAuth";
import { Input } from "../../Components/Input";
import { Notification } from "../../Components/Notifications";
import { eventBus } from "../../Common/EventBus";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ErrorMessage } from "../../Components/ErrorMessage";
import { validatePasswords } from "../../Common/Utils/Validators";
import { isErr } from "../../Common/Utils/Result";
import { getErrorMessage } from "../../Common/Utils/errorMessages";
import { LoginWrapper, LoginDisplayer, LoginFormWrapper, LoginButton, SecondaryButton } from "../Login/Styles";
import { TbLock } from "react-icons/tb";
import { RotatingLoaderIcon } from "../../Components/Button/Styles";
import { HiArrowRight } from "react-icons/hi2";
import { AnimatedBackground } from "./Styles";

import MikeLogo from "../../Assets/logo.png";

export const ResetPassword = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const { resetPassword, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const navigate = useNavigate();

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    setApiError("");
    setPasswordError("");

    const { isValid, errorKey } = validatePasswords(password, confirmPassword);
    if (!isValid) {
      setPasswordError(t(errorKey));
      return;
    }

    setLoading(true);
    const response = await resetPassword(token || '', email || '', password);
    setLoading(false);

    if (isErr(response)) {
      const errorMessage = getErrorMessage(response.error?.errorCode);
      setApiError(errorMessage);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", errorMessage)
      );
      return;
    }

    logout();
    setPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setApiError("");

    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification("success", t("auth.resetPassword.successNotification"))
    );

    window.location.href = '/login';
  };

  return (
    <LoginWrapper>
      <AnimatedBackground>
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
      </AnimatedBackground>
      <img src={MikeLogo} alt={t("auth.resetPassword.mikeLogoAlt")} />
      <LoginDisplayer>
        <LoginFormWrapper>
          <h1 style={{ margin: 0, marginBottom: "1rem", textAlign: "center" }}>{t("auth.resetPassword.title")}</h1>
          <p style={{ textAlign: "center", marginBottom: "1rem", color: "#565869" }}>
            {t("auth.resetPassword.subtitle")}
          </p>
          <p style={{ textAlign: "center", marginBottom: "1.5rem", fontWeight: 600 }}>
            {email}
          </p>
          <Input
            Icon={TbLock}
            placeholder={t("auth.resetPassword.passwordPlaceholder")}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!passwordError || !!apiError}
            onEnterPress={async () => await handleSubmit()}
          />
          <Input
            Icon={TbLock}
            placeholder={t("auth.resetPassword.confirmPasswordPlaceholder")}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!passwordError || !!apiError}
            onEnterPress={async () => await handleSubmit()}
          />
          <ErrorMessage errorMessage={passwordError} />
          {apiError && <ErrorMessage errorMessage={apiError} />}
          <LoginButton
            type="button"
            onClick={() => handleSubmit()}
            disabled={loading}
            $isLoading={loading}
          >
            {loading && <RotatingLoaderIcon />}
            {loading ? t("auth.resetPassword.submitButtonLoading") : (
              <>
                <span>{t("auth.resetPassword.submitButton")}</span>
                <HiArrowRight />
              </>
            )}
          </LoginButton>
          <SecondaryButton onClick={() => navigate('/login')} style={{ marginTop: "0.5rem" }}>
            {t("auth.resetPassword.backButton")}
          </SecondaryButton>
        </LoginFormWrapper>
      </LoginDisplayer>
    </LoginWrapper>
  );
};
